import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Typography,
  Checkbox,
  Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import InboxIcon from '@mui/icons-material/Inbox';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { formatDate, formatMoney, parseEuroNumber } from '../utils/helper';
import StatusChip from './StatusChip';
import { COMMISSION_STATUS } from '../constants';

const CommissionTable = ({
  monthData,
  onDelete,
  onEdit,
  onUnlock,
  onShowHistory,
  onTogglePaid,
  invoiceNumber,
  setInvoiceNumber,
  onDownloadInvoice,
  showInvoiceFields,
  isEditable,
  isAdmin,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const handleDownload = () => {
    if (!invoiceNumber) {
      setOpenDialog(true);
    } else {
      onDownloadInvoice();
    }
  };

  const proceedWithoutInvoiceNumber = () => {
    setOpenDialog(false);
    onDownloadInvoice();
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
  };

  const confirmDelete = () => {
    onDelete(deleteRow);
    setDeleteRow(null);
  };

  const cancelDelete = () => {
    setDeleteRow(null);
  };

  const truncateText = (text, limit = 15) => {
    return text?.length > limit ? text.slice(0, limit) + '...' : text;
  };

  const showEditDelete = isEditable || isAdmin;

  // Always: Client, Prospect, Event, Commission, Submitted Date, Status, Notes, History
  const emptyColSpanCount =
    8 +
    (isAdmin ? 1 : 0) + // Lead Agent
    (showEditDelete ? 2 : 0) + // Edit, Delete
    (isAdmin ? 1 : 0) + // Paid
    (isAdmin && !isEditable ? 1 : 0); // Unlock

  return (
    <>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: '900px', lg: '100%' } }}>
          <TableHead>
            <TableRow>
              {isAdmin ? <TableCell sx={{ whiteSpace: 'nowrap' }}>Lead Agent</TableCell> : null}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Client</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Prospect</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Event</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Commission</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Submitted</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Notes</TableCell>
              {showEditDelete ? (
                <>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Edit</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Delete</TableCell>
                </>
              ) : null}
              <TableCell sx={{ whiteSpace: 'nowrap' }}>History</TableCell>
              {isAdmin ? <TableCell sx={{ whiteSpace: 'nowrap' }}>Paid</TableCell> : null}
              {isAdmin && !isEditable ? <TableCell sx={{ whiteSpace: 'nowrap' }}>Unlock</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {monthData?.length > 0 ? (
              monthData.map((row, index) => {
                const intPart = parseEuroNumber(row.commission);
                const decPart = parseInt(row.cents || '0', 10);
                const fullAmount = intPart + decPart / 100;
                const formattedAmount = formatMoney(fullAmount);

                return (
                  <TableRow key={row._id || index} sx={{ '& > *': { py: 1.5 } }}>
                    {isAdmin ? (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={`${row.leadAgent?.firstName || ''} ${row.leadAgent?.lastName || ''}`} arrow>
                          <span>
                            {truncateText(`${row.leadAgent?.firstName || ''} ${row.leadAgent?.lastName || ''}`)}
                          </span>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={`${row.client?.company?.companyName}`} arrow>
                        <span>{truncateText(`${row.client?.company?.companyName}`)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={row.prospect} arrow>
                        <span>{truncateText(row.prospect)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={row.event === 'Other' && row.otherEvent ? row.otherEvent : row.event} arrow>
                        <span>
                          {truncateText(row.event === 'Other' && row.otherEvent ? row.otherEvent : row.event)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formattedAmount}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.submittedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box display='inline-flex' alignItems='center' gap={1}>
                        <StatusChip status={row.status} />
                        {row.status === COMMISSION_STATUS.DECLINED && (
                          <Tooltip title={row.declineReason}>
                            <InfoIcon fontSize='small' color='action' />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.notes ? (
                        <Tooltip title={row.notes} arrow>
                          <span>{truncateText(row.notes)}</span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    {showEditDelete ? (
                      <>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton onClick={() => onEdit(row)}>
                            <EditIcon color='primary' />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton onClick={() => handleDeleteClick(row)}>
                            <DeleteIcon color='error' />
                          </IconButton>
                        </TableCell>
                      </>
                    ) : null}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Link component='button' type='button' underline='hover' onClick={() => onShowHistory(row)}>
                        Show
                      </Link>
                    </TableCell>
                    {isAdmin ? (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Checkbox
                          checked={Boolean(row.paid)}
                          disabled={!row.locked}
                          onChange={(e) => onTogglePaid(row, e.target.checked)}
                          inputProps={{ 'aria-label': 'Paid' }}
                        />
                      </TableCell>
                    ) : null}
                    {isAdmin && !isEditable ? (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton onClick={() => onUnlock(row)} disabled={!row.locked}>
                          <LockOpenIcon color={row.locked ? 'primary' : 'disabled'} />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={emptyColSpanCount} align='center'>
                  <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                    <InboxIcon color='disabled' />
                    <Typography variant='body2' color='text.secondary'>
                      No commissions submitted.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showInvoiceFields && !isAdmin && (
        <Box mt={2} display='flex' justifyContent='flex-end' gap={2}>
          <TextField
            size='small'
            label='Your invoice number'
            variant='outlined'
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            sx={{ width: '250px' }}
          />
          <Button variant='outlined' color='primary' size='small' onClick={handleDownload}>
            Download Invoice
          </Button>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>No Invoice Number</DialogTitle>
        <DialogContent>You haven't added an invoice number. Continue anyway?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color='secondary'>
            Go Back
          </Button>
          <Button onClick={proceedWithoutInvoiceNumber} color='primary' variant='contained'>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteRow !== null} onClose={cancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete this commission?</DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color='secondary'>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color='error' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommissionTable;
